/**
 * Pharmacy Questionnaire Component
 * Handles question navigation, validation, and submission via Klaviyo Client API
 */

class TypeformQuiz {
  constructor(container) {
    this.container = container;
    this.quizId = container.dataset.quizId;
    try {
      this.questions = JSON.parse(container.dataset.questions || "[]");
    } catch (e) {
      console.error("Failed to parse questions JSON:", e, container.dataset.questions);
      this.questions = [];
    }
    this.klaviyoKey = (container.dataset.klaviyoKey || "").trim();
    this.serviceName = container.dataset.serviceName;
    this.successMessage = container.dataset.successMessage;
    const minSubmitSeconds = Number(container.dataset.minSubmitSeconds);
    this.minSubmitMs = Number.isFinite(minSubmitSeconds) && minSubmitSeconds >= 0
      ? minSubmitSeconds * 1000
      : 4000;
    this.startedAt = Date.now();
    this.isSubmitting = false;

    this.currentIndex = 0;
    this.answers = {};
    this.elements = {
      questionsContainer: container.querySelector(".quiz-questions"),
      prevBtn: container.querySelector(".quiz-btn-prev"),
      nextBtn: container.querySelector(".quiz-btn-next"),
      submitBtn: container.querySelector(".quiz-btn-submit"),
      progressBar: container.querySelector(".quiz-progress-bar"),
      currentQuestion: container.querySelector(".current-question"),
      totalQuestions: container.querySelector(".total-questions"),
      loading: container.querySelector(".quiz-loading"),
      success: container.querySelector(".quiz-success"),
      navigation: container.querySelector(".quiz-navigation"),
      honeypot: container.querySelector("[data-quiz-honeypot]"),
    };

    this.init();
  }

  init() {
    if (this.questions.length === 0) {
      this.showError("No questions configured");
      return;
    }

    this.renderQuestions();
    this.attachEventListeners();
    this.loadDraft();
    this.updateUI();

    // Update total questions count (exclude intro from count)
    this.hasIntro = this.questions[0]?.type === "intro";
    const questionCount = this.hasIntro ? this.questions.length - 1 : this.questions.length;
    this.elements.totalQuestions.textContent = questionCount;
  }

  /**
   * Get the display question number (excludes intro from numbering).
   */
  getQuestionNumber(index) {
    return this.questions[0]?.type === "intro" ? index : index + 1;
  }

  renderQuestions() {
    this.questions.forEach((question, index) => {
      const questionEl = document.createElement("div");
      questionEl.className = "quiz-question";
      questionEl.dataset.index = index;

      if (index === 0) questionEl.classList.add("active");

      const questionNumber = this.getQuestionNumber(index);

      if (question.type === "intro") {
        questionEl.classList.add("quiz-intro");
        questionEl.innerHTML = `
          <h2 class="quiz-intro-title">${question.question}</h2>
          ${question.steps ? `
            <div class="quiz-intro-steps">
              ${question.steps.map((step, i) => `
                <div class="quiz-intro-step">
                  <div class="quiz-intro-step-number">${i + 1}</div>
                  <div class="quiz-intro-step-content">
                    <h3 class="quiz-intro-step-title">${step.title}</h3>
                    <p class="quiz-intro-step-desc">${step.description}</p>
                  </div>
                </div>
              `).join("")}
            </div>
          ` : ""}
          <button type="button" class="quiz-btn quiz-btn-start">
            ${question.button_text || "Let's start"}
          </button>
        `;
      } else if (question.type === "group") {
        questionEl.innerHTML = `
          <div class="quiz-question-number">Question ${questionNumber} →</div>
          <h2 class="quiz-question-title">${question.question}</h2>
          <div class="quiz-group">
            ${question.fields.map((field) => `
              <div class="quiz-group-field" data-field-id="${field.id}">
                <label class="quiz-group-label">${field.question}${field.required ? ' <span class="quiz-required">*</span>' : ""}</label>
                ${this.renderInput(field)}
                ${field.required ? '<div class="quiz-error" style="display:none;">This field is required</div>' : ""}
              </div>
            `).join("")}
          </div>
        `;
      } else {
        questionEl.innerHTML = `
          <div class="quiz-question-number">Question ${questionNumber} →</div>
          <h2 class="quiz-question-title">${question.question}</h2>
          ${this.renderInput(question)}
          ${question.required ? '<div class="quiz-error" style="display:none;">This question is required</div>' : ""}
        `;
      }

      this.elements.questionsContainer.appendChild(questionEl);
    });
  }

  renderInput(question) {
    const value = this.answers[question.id] || "";

    switch (question.type) {
      case "text":
      case "email":
      case "tel":
      case "number":
        return `
          <input
            type="${question.type}"
            class="quiz-input"
            data-question-id="${question.id}"
            placeholder="${question.placeholder || ""}"
            value="${value}"
            ${question.required ? "required" : ""}
          />
        `;

      case "textarea":
        return `
          <textarea
            class="quiz-textarea"
            data-question-id="${question.id}"
            placeholder="${question.placeholder || ""}"
            ${question.required ? "required" : ""}
          >${value}</textarea>
        `;

      case "choice":
        return `
          <div class="quiz-choices" data-question-id="${question.id}">
            ${question.options
              .map(
                (option) => `
              <button
                type="button"
                class="quiz-choice ${value === option ? "selected" : ""}"
                data-value="${option}"
              >
                <span class="quiz-choice-radio"></span>
                <span>${option}</span>
              </button>
            `,
              )
              .join("")}
          </div>
        `;

      case "checkbox":
        const selected = Array.isArray(value) ? value : [];
        return `
          <div class="quiz-checkboxes" data-question-id="${question.id}">
            ${question.options
              .map(
                (option) => `
              <button
                type="button"
                class="quiz-checkbox ${selected.includes(option) ? "selected" : ""}"
                data-value="${option}"
              >
                <span class="quiz-checkbox-box"></span>
                <span>${option}</span>
              </button>
            `,
              )
              .join("")}
          </div>
        `;

      case "dropdown":
        return `
          <select
            class="quiz-select"
            data-question-id="${question.id}"
            ${question.required ? "required" : ""}
          >
            <option value="" disabled ${!value ? "selected" : ""}>${question.placeholder || "Select an option"}</option>
            ${question.options
              .map(
                (option) => `
              <option value="${option}" ${value === option ? "selected" : ""}>${option}</option>
            `,
              )
              .join("")}
          </select>
        `;

      case "date":
        return `
          <input
            type="date"
            class="quiz-input"
            data-question-id="${question.id}"
            value="${value}"
            ${question.required ? "required" : ""}
          />
        `;

      case "rating":
        const max = question.max || 5;
        return `
          <div class="quiz-rating" data-question-id="${question.id}">
            ${Array.from({ length: max }, (_, i) => i + 1)
              .map(
                (num) => `
              <button
                type="button"
                class="quiz-rating-btn ${value == num ? "selected" : ""}"
                data-value="${num}"
              >
                ${num}
              </button>
            `,
              )
              .join("")}
          </div>
        `;

      default:
        return `<input type="text" class="quiz-input" data-question-id="${question.id}" value="${value}" />`;
    }
  }

  attachEventListeners() {
    // Start button (intro page)
    this.container.addEventListener("click", (e) => {
      if (e.target.closest(".quiz-btn-start")) {
        this.navigateToQuestion(1);
      }
    });

    // Navigation buttons
    this.elements.prevBtn.addEventListener("click", () =>
      this.previousQuestion(),
    );
    this.elements.nextBtn.addEventListener("click", () => this.nextQuestion());
    this.elements.submitBtn.addEventListener("click", () => this.submit());

    // Input changes (text, email, tel, number, date, textarea)
    this.container.addEventListener("input", (e) => {
      if (e.target.matches(".quiz-input, .quiz-textarea")) {
        const questionId = e.target.dataset.questionId;
        this.answers[questionId] = e.target.value;
        this.saveDraft();
        this.clearError();
      }
    });

    // Dropdown changes
    this.container.addEventListener("change", (e) => {
      if (e.target.matches(".quiz-select")) {
        const questionId = e.target.dataset.questionId;
        this.answers[questionId] = e.target.value;
        this.saveDraft();
        this.clearError();
      }
    });

    // Choice selection
    this.container.addEventListener("click", (e) => {
      if (e.target.closest(".quiz-choice")) {
        const button = e.target.closest(".quiz-choice");
        const container = button.parentElement;
        const questionId = container.dataset.questionId;
        const value = button.dataset.value;

        // Deselect all
        container.querySelectorAll(".quiz-choice").forEach((btn) => {
          btn.classList.remove("selected");
        });

        // Select this one
        button.classList.add("selected");
        this.answers[questionId] = value;
        this.saveDraft();
        this.clearError();
      }

      // Checkbox toggle (multi-select)
      if (e.target.closest(".quiz-checkbox")) {
        const button = e.target.closest(".quiz-checkbox");
        const container = button.parentElement;
        const questionId = container.dataset.questionId;
        const value = button.dataset.value;

        button.classList.toggle("selected");

        // Collect all selected values
        const selectedValues = [];
        container.querySelectorAll(".quiz-checkbox.selected").forEach((btn) => {
          selectedValues.push(btn.dataset.value);
        });
        this.answers[questionId] = selectedValues;
        this.saveDraft();
        this.clearError();
      }

      // Rating buttons
      if (e.target.closest(".quiz-rating-btn")) {
        const button = e.target.closest(".quiz-rating-btn");
        const container = button.parentElement;
        const questionId = container.dataset.questionId;
        const value = button.dataset.value;

        container.querySelectorAll(".quiz-rating-btn").forEach((btn) => {
          btn.classList.remove("selected");
        });

        button.classList.add("selected");
        this.answers[questionId] = value;
        this.saveDraft();
        this.clearError();
      }
    });

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (!this.container.contains(document.activeElement)) return;

      if (e.key === "Enter" && !e.shiftKey && !e.target.matches("textarea")) {
        e.preventDefault();
        if (this.currentIndex < this.questions.length - 1) {
          this.nextQuestion();
        } else {
          this.submit();
        }
      }
    });
  }

  nextQuestion() {
    if (!this.validateCurrentQuestion()) {
      return;
    }

    if (this.currentIndex < this.questions.length - 1) {
      this.navigateToQuestion(this.currentIndex + 1);
    }
  }

  previousQuestion() {
    if (this.currentIndex > 0) {
      this.navigateToQuestion(this.currentIndex - 1, "prev");
    }
  }

  navigateToQuestion(index, direction = "next") {
    const currentQ = this.container.querySelector(
      `[data-index="${this.currentIndex}"]`,
    );
    const nextQ = this.container.querySelector(`[data-index="${index}"]`);

    currentQ.classList.remove("active");
    if (direction === "prev") {
      currentQ.classList.add("next");
      nextQ.classList.add("prev");
    } else {
      currentQ.classList.add("prev");
      nextQ.classList.remove("prev");
    }

    setTimeout(() => {
      currentQ.classList.remove("active", "prev", "next");
      nextQ.classList.remove("prev", "next");
      nextQ.classList.add("active");
      this.currentIndex = index;
      this.updateUI();

      // Scroll to top of quiz instantly, offset up to clear Shopify header
      const rect = this.container.getBoundingClientRect();
      window.scrollBy({ top: rect.top - 20, behavior: "instant" });

      // Focus first input in new question
      const input = nextQ.querySelector(".quiz-input, .quiz-textarea");
      if (input) input.focus();
    }, 50);
  }

  validateCurrentQuestion() {
    const question = this.questions[this.currentIndex];

    if (question.type === "intro") return true;

    if (question.type === "group") {
      for (const field of question.fields) {
        if (!this.validateField(field)) return false;
      }
      return true;
    }

    return this.validateField(question);
  }

  validateField(field) {
    const answer = this.answers[field.id];

    if (field.required && (!answer || (Array.isArray(answer) ? answer.length === 0 : answer.trim() === ""))) {
      this.showFieldError(field.id, "This field is required");
      return false;
    }

    if (field.type === "email" && answer) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(answer)) {
        this.showFieldError(field.id, "Please enter a valid email address");
        return false;
      }
    }

    return true;
  }

  showFieldError(fieldId, message) {
    const currentQ = this.container.querySelector(
      `[data-index="${this.currentIndex}"]`,
    );
    // For group fields, find the specific field container
    const fieldEl = currentQ?.querySelector(`[data-field-id="${fieldId}"]`);
    const errorEl = fieldEl
      ? fieldEl.querySelector(".quiz-error")
      : currentQ?.querySelector(".quiz-error");
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = "block";
    }
  }

  showError(message) {
    const currentQ = this.container.querySelector(
      `[data-index="${this.currentIndex}"]`,
    );
    const errorEl = currentQ?.querySelector(".quiz-error");
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = "block";
    }
  }

  clearError() {
    const currentQ = this.container.querySelector(
      `[data-index="${this.currentIndex}"]`,
    );
    if (currentQ) {
      currentQ.querySelectorAll(".quiz-error").forEach((el) => {
        el.style.display = "none";
      });
    }
  }

  updateUI() {
    const isIntro = this.questions[this.currentIndex]?.type === "intro";
    const offset = this.hasIntro ? 1 : 0;
    const totalQuestions = this.questions.length - offset;

    // Hide progress and nav on intro page
    this.container.querySelector(".quiz-progress").style.display = isIntro ? "none" : "";
    this.elements.navigation.style.display = isIntro ? "none" : "flex";

    // Update progress (adjusted for intro offset)
    const currentNum = this.currentIndex + 1 - offset;
    const progress = (currentNum / totalQuestions) * 100;
    this.elements.progressBar.style.width = `${progress}%`;
    this.elements.currentQuestion.textContent = currentNum;

    // Update buttons — don't go back to intro
    this.elements.prevBtn.disabled = this.isSubmitting || this.currentIndex <= offset;

    const isLastQuestion = this.currentIndex === this.questions.length - 1;
    this.elements.nextBtn.style.display = isLastQuestion ? "none" : "block";
    this.elements.submitBtn.style.display = isLastQuestion ? "block" : "none";
    this.elements.nextBtn.disabled = this.isSubmitting;
    this.elements.submitBtn.disabled = this.isSubmitting;
  }

  /**
   * Find the customer email from the answers by looking for an email-type question.
   */
  getCustomerEmail() {
    for (const question of this.questions) {
      if (question.type === "email") {
        return this.answers[question.id] || null;
      }
      if (question.type === "group") {
        const emailField = question.fields.find((f) => f.type === "email");
        if (emailField) return this.answers[emailField.id] || null;
      }
    }
    return null;
  }

  formatAnswerValue(value) {
    if (Array.isArray(value)) return value.join(", ");
    if (value === null || value === undefined || value === "") return "Not provided";
    return String(value);
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  /**
   * Build answers in multiple shapes:
   * - byQuestion: object keyed by question text (current behavior)
   * - ordered: array preserving metaobject question order
   * - text: human-readable multiline string (question + answer on separate lines)
   * - html: HTML-ready string with <br> line breaks
   */
  buildAnswersPayload() {
    const byQuestion = {};
    const ordered = [];
    let position = 1;

    const pushAnswer = (questionText, rawValue) => {
      byQuestion[questionText] = rawValue;
      ordered.push({
        position: position,
        question: questionText,
        answer: this.formatAnswerValue(rawValue),
        raw_answer: rawValue,
      });
      position += 1;
    };

    for (const question of this.questions) {
      if (question.type === "intro") continue;

      if (question.type === "group") {
        for (const field of question.fields) {
          if (this.answers[field.id] !== undefined) {
            pushAnswer(field.question, this.answers[field.id]);
          }
        }
      } else if (this.answers[question.id] !== undefined) {
        pushAnswer(question.question, this.answers[question.id]);
      }
    }

    const text = ordered
      .map((item) => `${item.position}. ${item.question}\n${item.answer}`)
      .join("\n");
    const html = ordered
      .map(
        (item) =>
          `${item.position}. ${this.escapeHtml(item.question)}<br>${this.escapeHtml(item.answer)}`,
      )
      .join("<br><br>");

    return { byQuestion, ordered, text, html };
  }

  async submit() {
    if (this.isSubmitting) {
      return;
    }

    if (!this.validateCurrentQuestion()) {
      return;
    }

    // Validate Klaviyo company_id is configured
    if (!this.klaviyoKey) {
      alert("Klaviyo API key is not configured. Please contact the site administrator.");
      return;
    }
    if (!/^[a-zA-Z0-9]{6}$/.test(this.klaviyoKey)) {
      alert(
        "Invalid Klaviyo Public API Key. Use the 6-character Company ID (Site ID) from Klaviyo settings.",
      );
      return;
    }

    // Validate that an email answer exists
    const customerEmail = this.getCustomerEmail();
    if (!customerEmail) {
      alert("Please provide your email address before submitting.");
      return;
    }

    // Simple anti-spam checks: hidden honeypot + minimum completion time.
    const honeypotValue = this.elements.honeypot?.value?.trim();
    if (honeypotValue) {
      console.warn("Blocked questionnaire submission due to honeypot value.");
      return;
    }
    if (Date.now() - this.startedAt < this.minSubmitMs) {
      const waitSeconds = Math.ceil(this.minSubmitMs / 1000);
      alert(`Please review your answers and submit after at least ${waitSeconds} seconds.`);
      return;
    }

    this.isSubmitting = true;
    this.updateUI();

    // Show loading
    this.elements.questionsContainer.style.display = "none";
    this.elements.navigation.style.display = "none";
    this.elements.loading.style.display = "block";

    try {
      const answersPayload = this.buildAnswersPayload();
      const revision = "2024-10-15";
      const companyId = encodeURIComponent(this.klaviyoKey);
      const clientHeaders = {
        "Content-Type": "application/json",
        Accept: "application/json",
        revision: revision,
      };

      // Step 1: Create/identify profile in Klaviyo
      const profileResponse = await fetch(
        `https://a.klaviyo.com/client/profiles/?company_id=${companyId}`,
        {
          method: "POST",
          headers: clientHeaders,
          body: JSON.stringify({
            data: {
              type: "profile",
              attributes: {
                email: customerEmail,
              },
            },
          }),
        },
      );
      if (!profileResponse.ok) {
        const details = await this.readResponseError(profileResponse);
        throw new Error(
          `Failed to create Klaviyo profile (${profileResponse.status}): ${details}`,
        );
      }

      // Step 2: Track "Questionnaire Submitted" event
      const eventResponse = await fetch(
        `https://a.klaviyo.com/client/events/?company_id=${companyId}`,
        {
          method: "POST",
          headers: clientHeaders,
          body: JSON.stringify({
            data: {
              type: "event",
              attributes: {
                metric: {
                  data: {
                    type: "metric",
                    attributes: {
                      name: "Questionnaire Submitted",
                    },
                  },
                },
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email: customerEmail,
                    },
                  },
                },
                properties: {
                  service_name: this.serviceName,
                  answers: answersPayload.byQuestion,
                  answers_ordered: answersPayload.ordered,
                  answers_text: answersPayload.text,
                  answers_html: answersPayload.html,
                  page_url: window.location.href,
                  timestamp: new Date().toISOString(),
                },
              },
            },
          }),
        },
      );

      if (!eventResponse.ok) {
        const details = await this.readResponseError(eventResponse);
        throw new Error(
          `Failed to submit questionnaire event (${eventResponse.status}): ${details}`,
        );
      }

      // Clear draft
      this.clearDraft();

      // Show success
      this.showSuccess();
    } catch (error) {
      console.error("Submission error:", error);
      alert(
        "Sorry, there was an error submitting your response. Please try again.",
      );

      // Restore UI
      this.isSubmitting = false;
      this.elements.loading.style.display = "none";
      this.elements.questionsContainer.style.display = "block";
      this.elements.navigation.style.display = "flex";
      this.updateUI();
    }
  }

  async readResponseError(response) {
    try {
      const data = await response.json();
      return JSON.stringify(data);
    } catch (jsonError) {
      try {
        return await response.text();
      } catch (textError) {
        return response.statusText || "Unknown error";
      }
    }
  }

  showSuccess() {
    this.elements.loading.style.display = "none";
    this.elements.success.style.display = "block";
    this.elements.success.querySelector(".quiz-success-text").textContent =
      this.successMessage;
  }

  saveDraft() {
    if (this.container.dataset.saveDraft === "true") {
      localStorage.setItem(
        `quiz_draft_${this.quizId}`,
        JSON.stringify({
          answers: this.answers,
          currentIndex: this.currentIndex,
          timestamp: Date.now(),
        }),
      );
    }
  }

  loadDraft() {
    if (this.container.dataset.saveDraft === "true") {
      const draft = localStorage.getItem(`quiz_draft_${this.quizId}`);
      if (draft) {
        try {
          const data = JSON.parse(draft);
          // Only load if less than 24 hours old
          if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
            this.answers = data.answers;
            // Re-render to populate saved answers
            this.elements.questionsContainer.innerHTML = "";
            this.renderQuestions();
          }
        } catch (e) {
          console.error("Failed to load draft:", e);
        }
      }
    }
  }

  clearDraft() {
    localStorage.removeItem(`quiz_draft_${this.quizId}`);
  }
}

// Initialize all quiz instances on the page
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".typeform-quiz").forEach((container) => {
    new TypeformQuiz(container);
  });
});
